import * as React from 'react'
import { ReactSingleSelect, ISelectOption } from '../../../components/ReactSelect'
import { CreateEntityAssignment, IEntity, UpdateUser } from '@getgreenline/homi-shared'
import { inject, observer } from 'mobx-react'
import { CurrentCompanyStore } from '../../../stores/CurrentCompanyStore'
import { CurrentUserStore } from '../../../stores/CurrentUserStore'
import { Tooltip } from 'antd'
import { Routes } from '../../../constants/Routes'
import { ADMINISTRATOR } from './AddEmployee'
import { BlazeButton } from '../../../components/CustomButtons/BlazeButton'

interface Props {
  user: UpdateUser
}

interface InjectedProps {
  currentCompanyStore?: CurrentCompanyStore
  currentUserStore?: CurrentUserStore
}

@inject('currentCompanyStore', 'currentUserStore')
@observer
export class SecurityRoleOptions extends React.Component<Props & InjectedProps> {
  company = this.props.currentCompanyStore!.company!

  getLocationOptions = (companyId?: number): ISelectOption[] => {
    const companyEntities: IEntity[] = this.checkIfCompanyEntityShouldBeAdded(companyId)
      ? [
          {
            id: this.company.id,
            name: this.company.name + ' (all locations)',
            entityType: 'company',
            rootEntityId: this.company.id,
          },
        ]
      : []

    const notYetSelectedLocationEntities = this.allLocationEntities.filter(
      (entity) => !this.currentlyAssignedEntityIds.includes(entity.id) || entity.id === companyId,
    )

    const companyAndLocationEntities = [...companyEntities, ...notYetSelectedLocationEntities]

    return companyAndLocationEntities.map((location) => ({
      value: location.id,
      label: location.name,
    }))
  }

  getSecurityRoleOptions = (locationId?: number): ISelectOption[] => {
    const currentUser = this.props.currentUserStore!.currentUser
    const securityRoles = this.props.currentCompanyStore!.securityRoles || []

    if (!currentUser || !locationId || !securityRoles) {
      return []
    }

    /**
     * All Organization users will be considered Admins until we revisit - Sam 2021/09
     */
    const organizationEntity = currentUser.entityAssignments.find(
      (assignment) => assignment.entity.entityType === 'organization',
    )

    const currentUserEntityAssignment = currentUser.entityAssignments.find(
      (entityAssignment) => entityAssignment.entity.id === locationId,
    )

    const matchingOrgAdmin =
      organizationEntity?.entity.id === currentUserEntityAssignment?.entity.rootEntityId

    const filteredSecurityRoles = securityRoles.filter((securityRole) => {
      if (currentUserEntityAssignment) {
        if (securityRole.name === ADMINISTRATOR) {
          return matchingOrgAdmin || securityRole.id === currentUserEntityAssignment.securityRoleId
        }
      }
      return true
    })

    return filteredSecurityRoles.map((securityRole) => ({
      value: securityRole.id,
      label: securityRole.name,
    }))
  }

  checkIfCompanyEntityShouldBeAdded = (entityId?: number) => {
    return (
      (!this.isCompanyEntityIdAlreadySelected || this.company.id === entityId) &&
      (this.allAvailableEntityAssignmentIds.includes(this.company.id) ||
        this.props.currentUserStore!.isAdmin)
    )
  }

  addLocation = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault()
    e.stopPropagation()
    this.props.user.addAssignment(this.company)
  }

  removeEntityAssignment = (index: number) => {
    const assignments = this.companyEntityAssignments
    assignments.splice(index, 1)
    this.props.user.entityAssignments = assignments
  }

  get companyEntityAssignments(): CreateEntityAssignment[] {
    // `Company` is an "assigned" entity where as `Location` is a "computed" entity
    return this.props.user.entityAssignments.filter(
      (e) =>
        e.type === 'assigned' &&
        (e.entity.id === this.company.id ||
          !e.entity.id ||
          this.props.currentCompanyStore!.locations?.find(
            (location) => location.id === e.entity.id,
          )),
    )
  }

  get currentlyAssignedEntityIds(): Array<number | undefined> {
    /**
     * This includes
     * 1. `companyId` displayed as <company-name> (all locations) in the dropdown
     * 2. `locationIds`
     */
    return this.companyEntityAssignments.map((entityAssignment) => entityAssignment.entity.id)
  }

  get allAvailableEntityAssignmentIds(): number[] {
    return this.props.currentUserStore ? this.props.currentUserStore.entityAssignmentIds || [] : []
  }

  get allLocationEntities(): IEntity[] {
    const locations = this.props.currentCompanyStore
      ? this.props.currentCompanyStore.locations || []
      : []

    return locations.map((location) => ({
      id: location.id,
      name: location.name,
      entityType: 'location',
      rootEntityId: this.company.id,
    }))
  }

  get isCompanyEntityIdAlreadySelected(): boolean {
    return this.currentlyAssignedEntityIds.includes(this.company.id)
  }

  render() {
    return (
      <>
        <div>
          <label className='font-weight-normal primary-font-black'>
            Security roles
            <Tooltip title='Only showing locations you are assigned to' placement='right'>
              <span className='fa fa-info-circle text-muted ml-1' />
            </Tooltip>
          </label>

          <p className='text-muted mb-2 primary-font-size'>
            Security roles can be edited{' '}
            <a href={Routes.DASHBOARD_EMPLOYEES_SECURITY_ROLES(this.company.id)}>here</a>
          </p>

          {this.companyEntityAssignments.map((entityAssignment, index) => {
            const entityId = entityAssignment.entity.id

            return (
              <div key={index} className='form-group row'>
                <div className='col-lg-3 col-md-5'>
                  <ReactSingleSelect
                    initialValue={entityId}
                    placeholder='No location selected'
                    options={this.getLocationOptions(entityId)}
                    onChange={(option: { label: string; value: number } | null) => {
                      entityAssignment.entity = {
                        id: option ? option.value : undefined,
                      }
                    }}
                  />
                </div>
                <div className='col-md-3'>
                  <ReactSingleSelect
                    initialValue={entityAssignment.securityRoleId}
                    placeholder='No security role selected'
                    options={this.getSecurityRoleOptions(entityId)}
                    onChange={(option: { label: string; value: number } | null) => {
                      entityAssignment.securityRoleId = option ? option.value : undefined
                    }}
                  />
                </div>
                <div className='col-md-2'>
                  <button
                    className='btn btn-light btn-circle shadow-sm mt-1'
                    onClick={() => this.removeEntityAssignment(index)}
                  >
                    <span className='fa fa-times' />
                  </button>
                </div>
              </div>
            )
          })}
        </div>

        <div className='mb-5'>
          {this.getLocationOptions().length > 0 && (
            <BlazeButton
              buttonProps={{
                type: 'default',
                size: 'large',
                onClick: this.addLocation,
              }}
              titleProps={{
                title: 'Add location',
              }}
              iconProps={{
                icon: 'plus',
              }}
            />
          )}
        </div>
      </>
    )
  }
}
